import React, { useState, useMemo, createContext, useContext } from 'react';
import { HashRouter, Routes, Route } from 'react-router-dom';
import '@fontsource/quicksand/500.css';
import '@fontsource/quicksand/600.css';
import '@fontsource/quicksand/700.css';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Container,
  Box,
  Typography
} from '@mui/material';
import Nav from './components/Nav';
import Home from './pages/Home';
import Projects from './pages/Projects';
import { pinkGradient } from './colors';

const ColorModeContext = createContext({ toggleColorMode: () => {} });

const sparkles = ['✨', '💕', '⭐', '🌸', '💫'];

function FloatingDots() {
  const [dots] = useState(() =>
    Array.from({ length: 16 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      size: 10 + Math.random() * 10,
      duration: 8 + Math.random() * 12,
      delay: Math.random() * 8,
      opacity: 0.2 + Math.random() * 0.3,
      glyph: sparkles[i % sparkles.length]
    }))
  );

  return (
    <Box sx={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0, overflow: 'hidden' }}>
      {dots.map(d => (
        <Box
          key={d.id}
          component="span"
          sx={{
            position: 'absolute',
            left: `${d.left}%`,
            bottom: -20,
            fontSize: d.size,
            opacity: d.opacity,
            animation: `floatUp ${d.duration}s ${d.delay}s infinite ease-in`,
            '@keyframes floatUp': {
              '0%': { transform: 'translateY(0) rotate(0deg)', opacity: d.opacity },
              '80%': { opacity: d.opacity },
              '100%': { transform: 'translateY(-100vh) rotate(25deg)', opacity: 0 }
            }
          }}
        >
          {d.glyph}
        </Box>
      ))}
    </Box>
  );
}

export default function App() {
  const [mode, setMode] = useState('light');
  const colorMode = useMemo(
    () => ({ toggleColorMode: () => setMode(m => (m === 'light' ? 'dark' : 'light')) }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: { main: '#c77dba' },
          secondary: { main: '#2d2d2d' }
        },
        shape: { borderRadius: 14 },
        typography: {
          fontFamily: 'Poppins, sans-serif',
          h4: { fontFamily: 'Quicksand, sans-serif', fontWeight: 700 },
          h6: { fontFamily: 'Quicksand, sans-serif', fontWeight: 700 },
          button: { textTransform: 'none', fontWeight: 600 }
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: { transition: 'background-color 0.4s ease, color 0.4s ease' }
            }
          }
        }
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <HashRouter>
          <FloatingDots />
          <Box sx={{ height: 4, background: pinkGradient }} />
          <Nav />
          <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 }, minHeight: '100vh', position: 'relative', zIndex: 1 }}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/projects" element={<Projects />} />
            </Routes>

            <Box sx={{ textAlign: 'center', mt: 4, pb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                © {new Date().getFullYear()} Cristina Fortiz
              </Typography>
            </Box>
          </Container>
          <Box sx={{ height: 4, background: pinkGradient }} />
        </HashRouter>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
}

export const useColorMode = () => useContext(ColorModeContext);
